/**
 * User utility functions for consistent user data handling across components
 * Consolidates user-related methods that were duplicated across 6+ components
 */

import { getUrl } from 'aws-amplify/storage';

/**
 * Get the user's display name with fallback logic
 * @param {Object} user - User object from Cognito attributes
 * @returns {string} User's display name
 */
export function getUserName(user) {
  if (!user?.attributes) return 'Anonymous User';
  
  // Priority 1: Direct name attribute
  if (user.attributes.name) {
    return user.attributes.name;
  }
  
  // Priority 2: Combine given + family name
  if (user.attributes.givenName && user.attributes.familyName) {
    return `${user.attributes.givenName} ${user.attributes.familyName}`;
  }
  
  // Priority 3: Just given name
  if (user.attributes.givenName) {
    return user.attributes.givenName;
  }
  
  // Priority 4: Just family name
  if (user.attributes.familyName) {
    return user.attributes.familyName;
  }
  
  // Priority 5: Email prefix (before @)
  if (user.attributes.email) {
    const emailPrefix = user.attributes.email.split('@')[0];
    return emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
  }
  
  // Final fallback
  return 'Anonymous User';
}

/**
 * Get the user's email address
 * @param {Object} user - User object from Cognito attributes
 * @returns {string} User's email address
 */
export function getUserEmail(user) {
  return user?.attributes?.email || '';
}

/**
 * Get the user's profile picture URL with fallback logic
 * @param {Object} user - User object from Cognito attributes
 * @returns {string|Promise<string>} Profile picture URL (may return a Promise for S3 paths)
 */
export function getUserPicture(user) {
  if (!user?.attributes) return '/images/default-avatar-silhouette.svg';
  
  // Priority 1: Custom profilePicture attribute
  if (user.attributes.profilePicture) {
    return user.attributes.profilePicture;
  }
  
  // Priority 2: Standard picture attribute (from OAuth providers or S3 path)
  if (user.attributes.picture) {
    // If it's an S3 path, generate a signed URL
    if (user.attributes.picture.startsWith('public/profile-pictures/')) {
      return getSignedProfilePictureUrl(user.attributes.picture);
    }
    
    return user.attributes.picture;
  }
  
  // Fallback to default
  return '/images/default-avatar-silhouette.svg';
}

/**
 * Get a signed URL for a profile picture S3 path
 * @param {string} s3Path - The S3 path to the profile picture
 * @returns {Promise<string>} Signed URL for the profile picture
 */
export async function getSignedProfilePictureUrl(s3Path) {
  try {
    const { url } = await getUrl({ path: s3Path });
    return url.toString();
  } catch (error) {
    return '/images/default-avatar-silhouette.svg';
  }
}

/**
 * Get user's initials for avatar fallback
 * @param {Object} user - User object from Cognito attributes
 * @returns {string} User's initials (2 characters max)
 */
export function getUserInitials(user) {
  const name = getUserName(user);
  
  if (name === 'Anonymous User') {
    return 'AU';
  }
  
  const nameParts = name.split(' ').filter(part => part.length > 0);
  
  if (nameParts.length >= 2) {
    return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
  } else if (nameParts.length === 1) {
    return nameParts[0].substring(0, 2).toUpperCase();
  }
  
  return 'AU';
}

/**
 * Check if user has a valid profile picture (not a placeholder)
 * @param {Object} user - User object from Cognito attributes
 * @returns {boolean} True if user has a real profile picture
 */
export function hasValidProfilePicture(user) {
  const picture = getUserPicture(user);
  
  if (!picture || picture === '/images/default-avatar-silhouette.svg') {
    return false;
  }
  
  // List of placeholder/invalid images to exclude
  const invalidPatterns = [
    '/images/small_profile_avatar.png',
    '/images/author-avatar1.png',
    '/images/author-avatar2.png',
    'default-avatar',
    'placeholder',
    'avatar-placeholder'
  ];
  
  return !invalidPatterns.some(pattern => picture.includes(pattern));
}

/**
 * Get user's display information as a complete object
 * @param {Object} user - User object from Cognito attributes
 * @returns {Object} User display information
 */
export function getUserDisplayInfo(user) {
  return {
    name: getUserName(user),
    email: getUserEmail(user),
    picture: getUserPicture(user),
    initials: getUserInitials(user),
    hasValidPicture: hasValidProfilePicture(user)
  };
}